
import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { ProtectedRoute, AuthRoute } from "../components/route-guards/RouteGuards";
import { AdminRoute } from "../components/AdminRoute";
import Auth from "../pages/Auth";
import NotFound from "../pages/NotFound";
import { AuthenticatedLayout, PublicLayout } from "../components/layouts/Layouts";
import ResetPassword from "../pages/ResetPassword";
import TabliWizz from "../pages/tabliwizz";
import FairClick from "../pages/fairclick";
import { usePiwikTracking } from "@/hooks/usePiwikTracking";
import { MetaTags } from "@/components/seo/MetaTags";

// Lazy load components to improve initial load time
const ShortUrlRedirect = lazy(() => import("../pages/shorturl-redirect"));
const Welcome = lazy(() => import("../pages/welcome"));
const TodoBoost = lazy(() => import("../pages/todo-boost"));
const RoiQuick = lazy(() => import("../pages/roi-quick"));
const ProfitPilot = lazy(() => import("../pages/profit-pilot"));
const EmailScore = lazy(() => import("../pages/email-score"));
const BizNameGen = lazy(() => import("../pages/biz-name-gen"));
const UtmMagic = lazy(() => import("../pages/utm-magic"));
const GenPersona = lazy(() => import("../pages/gen-persona"));
const SnippetView = lazy(() => import("../pages/snippet-view"));
const Converticalc = lazy(() => import("../pages/converticalc"));
const KeywordCheck = lazy(() => import("../pages/keyword-check"));
const Evorate = lazy(() => import("../pages/evorate"));
const FunnelFlow = lazy(() => import("../pages/funnel-flow"));
const Settings = lazy(() => import("../pages/settings"));
const Dashboard = lazy(() => import("../pages/dashboard"));
const Faq = lazy(() => import("../pages/faq"));
const Partners = lazy(() => import("../pages/partners"));
const Premium = lazy(() => import("../pages/premium"));
const Blog = lazy(() => import("../pages/blog"));
const ShortaTrack = lazy(() => import("../pages/shortatrack"));
const ShortaTrackStats = lazy(() => import("../pages/shortatrack-stats"));
const AdminDashboard = lazy(() => import("../pages/admin-dashboard"));
const BlogEditor = lazy(() => import("../pages/blog/editor"));
const CheckLista = lazy(() => import("../pages/checklista"));
const FormuGen = lazy(() => import("../pages/formugen"));
const QRna = lazy(() => import("../pages/qrna"));
const PageSpeedCheck = lazy(() => import("../pages/pagespeed-check"));
const CompressaPic = lazy(() => import("../pages/compressapic"));
const CanvasBuilder = lazy(() => import("../pages/canvasbuilder"));
const Planifica = lazy(() => import("../pages/planifica"));
const MesRapports = lazy(() => import("../pages/mes-rapports"));
const QuizNGo = lazy(() => import("../pages/quizngo"));
const QuizNGoPreview = lazy(() => import("../pages/quizngo/preview"));
const QuizNGoStats = lazy(() => import("../pages/quizngo/stats"));

const AppRoutes: React.FC = () => {
  // Utiliser le hook de tracking Piwik
  usePiwikTracking();
  
  return (
    <Routes>
      {/* Rediriger la page d'accueil vers welcome */}
      <Route path="/" element={<Navigate to="/welcome" replace />} />
      
      <Route path="/auth" element={<AuthRoute><Auth /></AuthRoute>} />
      
      <Route path="/auth/reset" element={<AuthRoute><ResetPassword /></AuthRoute>} />
      <Route path="/reset-password" element={<AuthRoute><ResetPassword /></AuthRoute>} />
      <Route path="/#/auth/reset" element={<AuthRoute><ResetPassword /></AuthRoute>} />
      <Route path="/#/auth/callback" element={<AuthRoute><ResetPassword /></AuthRoute>} />
      
      {/* Route de redirection pour URLs raccourcies - assurez-vous que le pattern est correct */}
      <Route path="/s/:short_code" element={
        <Suspense fallback={<div>Loading...</div>}>
          <ShortUrlRedirect />
        </Suspense>
      } />
      
      {/* Welcome est maintenant accessible sans authentification */}
      <Route path="/welcome" element={
        <Suspense fallback={<div>Loading...</div>}>
          <AuthenticatedLayout>
            <Welcome />
          </AuthenticatedLayout>
        </Suspense>
      } />
      
      {/* Routes d'outils accessibles sans authentification */}
      <Route element={<AuthenticatedLayout><Outlet /></AuthenticatedLayout>}>
        <Route path="/todo-boost" element={
          <Suspense fallback={<div>Loading...</div>}>
            <TodoBoost />
          </Suspense>
        } />
        <Route path="/roi-quick" element={
          <Suspense fallback={<div>Loading...</div>}>
            <RoiQuick />
          </Suspense>
        } />
        <Route path="/profit-pilot" element={
          <Suspense fallback={<div>Loading...</div>}>
            <ProfitPilot />
          </Suspense>
        } />
        <Route path="/email-score" element={
          <Suspense fallback={<div>Loading...</div>}>
            <EmailScore />
          </Suspense>
        } />
        <Route path="/biz-name-gen" element={
          <Suspense fallback={<div>Loading...</div>}>
            <BizNameGen />
          </Suspense>
        } />
        <Route path="/utm-magic" element={
          <Suspense fallback={<div>Loading...</div>}>
            <UtmMagic />
          </Suspense>
        } />
        <Route path="/gen-persona" element={
          <Suspense fallback={<div>Loading...</div>}>
            <GenPersona />
          </Suspense>
        } />
        <Route path="/snippet-view" element={
          <Suspense fallback={<div>Loading...</div>}>
            <SnippetView />
          </Suspense>
        } />
        <Route path="/converticalc" element={
          <Suspense fallback={<div>Loading...</div>}>
            <Converticalc />
          </Suspense>
        } />
        <Route path="/keyword-check" element={
          <Suspense fallback={<div>Loading...</div>}>
            <KeywordCheck />
          </Suspense>
        } />
        <Route path="/evorate" element={
          <Suspense fallback={<div>Loading...</div>}>
            <Evorate />
          </Suspense>
        } />
        <Route path="/funnel-flow" element={
          <Suspense fallback={<div>Loading...</div>}>
            <FunnelFlow />
          </Suspense>
        } />
        <Route path="/pagespeed-check" element={
          <Suspense fallback={<div>Loading...</div>}>
            <PageSpeedCheck />
          </Suspense>
        } />
        <Route path="/checklista" element={
          <Suspense fallback={<div>Loading...</div>}>
            <CheckLista />
          </Suspense>
        } />
        <Route path="/formugen" element={
          <Suspense fallback={<div>Loading...</div>}>
            <FormuGen />
          </Suspense>
        } />
        <Route path="/qrna" element={
          <Suspense fallback={<div>Loading...</div>}>
            <QRna />
          </Suspense>
        } />
        <Route path="/compressapic" element={
          <Suspense fallback={<div>Loading...</div>}>
            <CompressaPic />
          </Suspense>
        } />
        <Route path="/canvasbuilder" element={
          <Suspense fallback={<div>Loading...</div>}>
            <CanvasBuilder />
          </Suspense>
        } />
        <Route path="/shortatrack" element={
          <Suspense fallback={<div>Loading...</div>}>
            <ShortaTrack />
          </Suspense>
        } />
        <Route path="/shortatrack/stats/:id" element={
          <Suspense fallback={<div>Loading...</div>}>
            <ShortaTrackStats />
          </Suspense>
        } />
        <Route path="/planifica" element={
          <Suspense fallback={<div>Loading...</div>}>
            <Planifica />
          </Suspense>
        } />
        <Route path="/tabliwizz" element={
          <Suspense fallback={<div>Loading...</div>}>
            <TabliWizz />
          </Suspense>
        } />
        <Route path="/fairclick" element={
          <Suspense fallback={<div>Loading...</div>}>
            <FairClick />
          </Suspense>
        } />
        <Route path="/quizngo" element={
          <Suspense fallback={<div>Loading...</div>}>
            <QuizNGo />
          </Suspense>
        } />
        <Route path="/quizngo/:slug" element={
          <Suspense fallback={<div>Loading...</div>}>
            <QuizNGoPreview />
          </Suspense>
        } />
        <Route path="/quizngo/stats/:quizId" element={
          <Suspense fallback={<div>Chargement des statistiques...</div>}>
            <QuizNGoStats />
          </Suspense>
        } />
      
        {/* Routes protégées nécessitant une authentification */}
        <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
          <Route path="/settings" element={
            <Suspense fallback={<div>Loading...</div>}>
              <Settings />
            </Suspense>
          } />
          <Route path="/dashboard" element={
            <Suspense fallback={<div>Loading...</div>}>
              <Dashboard />
            </Suspense>
          } />
          <Route path="/faq" element={
            <Suspense fallback={<div>Loading...</div>}>
              <Faq />
            </Suspense>
          } />
          <Route path="/partners" element={
            <Suspense fallback={<div>Loading...</div>}>
              <Partners />
            </Suspense>
          } />
          <Route path="/premium" element={
            <Suspense fallback={<div>Loading...</div>}>
              <Premium />
            </Suspense>
          } />
          <Route path="/blog" element={
            <Suspense fallback={<div>Loading...</div>}>
              <Blog />
            </Suspense>
          } />
          <Route path="/blog/editor" element={
            <Suspense fallback={<div>Loading...</div>}>
              <BlogEditor />
            </Suspense>
          } />
          <Route path="/mes-rapports" element={
            <Suspense fallback={<div>Loading...</div>}>
              <MesRapports />
            </Suspense>
          } />
        </Route>

        {/* Route admin avec protection spécifique */}
        <Route path="/admin-dashboard" element={
          <Suspense fallback={<div>Vérification des droits administrateur...</div>}>
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          </Suspense>
        } />
      </Route>
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
